import { CloseButton } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Input } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { type FC, useEffect } from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { staffEmailCopy } from '@/app/helpers/staff-email.helpers.ts';
import toast from '@/app/toast.ts';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from '@/primitives/dialog.tsx';
import { useFetchEmailExistsQuery, useUpdateEmailMutation } from '@/slices/user-api-slice.ts';
import { emailPattern } from '../types/constants.ts';
import type { UserListModel, UserModel } from '../types/user-type';

export interface ChangeEmailDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  user: UserListModel | UserModel;
}

export const EditEmailDialog: FC<ChangeEmailDialogProps> = ({ isOpen, setIsOpen, user }) => {
  const { reset, handleSubmit, control, watch } = useForm<{
    email: string;
  }>();
  const emailWatch = watch('email');
  const { data: emailTaken } = useFetchEmailExistsQuery(
    emailWatch && emailWatch.match(emailPattern) ? emailWatch : skipToken,
  );
  const [patch, { isLoading, isSuccess, isError }] = useUpdateEmailMutation();

  const onSubmit: SubmitHandler<{ email: string }> = (value: { email: string }) => {
    patch({
      ...user,
      emailAddress: value.email,
    })
      .unwrap()
      .then(() => {
        reset();
        setIsOpen(false);
      });
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success('Email has been changed successfully');
    }

    if (isError) {
      toast.error('There was an error changing the email address.');
    }
  }, [isSuccess, isError]);

  return (
    <Dialog onClose={setIsOpen} open={isOpen}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-row justify-between'>
          <DialogTitle>Update Email</DialogTitle>
          <CloseButton onClick={() => reset()} as={Button} variant='light'>
            <XMarkIcon />
          </CloseButton>
        </div>
        <DialogDescription>
          Update email address for {user?.firstName} {user?.lastName}
        </DialogDescription>
        <DialogBody>
          <Controller
            name='email'
            control={control}
            rules={{
              required: 'This field is required',
              pattern: { value: emailPattern, message: 'Invalid email' },
              validate: val =>
                val && emailTaken?.exists
                  ? (emailTaken.message ?? staffEmailCopy.emailAlreadyUsed)
                  : undefined,
            }}
            render={({ field, fieldState }) => (
              <Input
                {...field}
                type='email'
                label='Email/Username'
                description={`Current email: ${user?.emailAddress}`}
                variant='bordered'
                isRequired
                isInvalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        </DialogBody>
        <DialogActions>
          <Button type='submit' color='primary' variant='solid' isLoading={isLoading}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
